/**
 * Room mapping configuration and utilities
 * 
 * Maps local room IDs (from ROOM_LADDER sourceRoom.id) to Mews RoomCategoryId values.
 * This mapping is configurable and can be updated when room categories change.
 * 
 * @module room-mapping
 */

/**
 * Room mapping configuration
 * 
 * Maps local room IDs (sourceRoom.id from ROOM_LADDER) to Mews RoomCategoryId.
 * 
 * This mapping should be configured based on actual Mews room categories.
 * Initially, this will be empty and populated after fetching hotel data from Mews API.
 * 
 * Format: { [localRoomId]: mewsRoomCategoryId }
 * 
 * Example:
 * {
 *   "junior-suite": "3edbe1b4-6739-40b7-81b3-d369d9469c48",
 *   "family-suite": "4fecf2c5-7840-51c8-92c4-e470ea057d59",
 *   ...
 * }
 */
export interface RoomMappingConfig {
  [localRoomId: string]: string; // localRoomId -> mewsRoomCategoryId
}

/**
 * Default room mapping configuration
 * 
 * This is a placeholder that should be populated from:
 * 1. Environment variables (VITE_MEWS_ROOM_MAPPING_JSON)
 * 2. API response matching (fuzzy matching by name)
 * 3. Manual configuration file
 * 
 * For now, this will be populated dynamically when hotels data is fetched.
 */
let roomMappingCache: RoomMappingConfig = {};

// Automatically load mapping from environment variable on module initialization
// This allows manual mappings to be configured via .env.local
if (typeof window !== 'undefined') {
  // Only run in browser environment
  loadRoomMappingFromEnv();
}

/**
 * Gets the current room mapping configuration
 * 
 * @returns Current room mapping configuration
 */
export function getRoomMapping(): RoomMappingConfig {
  return { ...roomMappingCache };
}

/**
 * Sets the room mapping configuration
 * 
 * @param mapping - Room mapping configuration to set
 */
export function setRoomMapping(mapping: RoomMappingConfig): void {
  roomMappingCache = { ...mapping };
}

/**
 * Clears the room mapping cache
 */
export function clearRoomMapping(): void {
  roomMappingCache = {};
}

/**
 * Gets Mews RoomCategoryId for a local room ID
 * 
 * @param localRoomId - Local room identifier (sourceRoom.id from ROOM_LADDER)
 * @returns Mews RoomCategoryId or undefined if not found
 */
export function getMewsRoomCategoryId(localRoomId: string): string | undefined {
  return roomMappingCache[localRoomId];
}

/**
 * Gets local room ID for a Mews RoomCategoryId
 * 
 * @param mewsRoomCategoryId - Mews room category identifier
 * @returns Local room ID or undefined if not found
 */
export function getLocalRoomId(mewsRoomCategoryId: string): string | undefined {
  const entry = Object.entries(roomMappingCache).find(
    ([, mewsId]) => mewsId === mewsRoomCategoryId
  );
  return entry?.[0];
}

/**
 * Loads room mapping from environment variable
 * 
 * Expects VITE_MEWS_ROOM_MAPPING_JSON to contain a JSON string
 * with the room mapping configuration.
 * 
 * @returns True if mapping was loaded successfully, false otherwise
 */
export function loadRoomMappingFromEnv(): boolean {
  const envMapping = import.meta.env.VITE_MEWS_ROOM_MAPPING_JSON;
  if (!envMapping) {
    return false;
  }

  try {
    const mapping = JSON.parse(envMapping) as RoomMappingConfig;
    setRoomMapping(mapping);
    return true;
  } catch (error) {
    console.warn(
      "[Room Mapping] Failed to parse VITE_MEWS_ROOM_MAPPING_JSON; live availability cannot map Mews categories.",
      import.meta.env.DEV ? error : "",
    );
    return false;
  }
}

/**
 * Attempts to auto-match local rooms to Mews room categories by name similarity
 * 
 * This is a fallback method when explicit mapping is not available.
 * Uses fuzzy matching on room names.
 * 
 * @param localRoomIds - Array of local room IDs to match
 * @param mewsRoomCategories - Array of Mews room categories
 * @param languageCode - Language code for localized names (default: "en-US")
 * @returns Room mapping configuration
 */
export function autoMatchRooms(
  localRoomIds: string[],
  mewsRoomCategories: Array<{ Id: string; Name: Record<string, string> }>,
  languageCode: string = 'en-US'
): RoomMappingConfig {
  const mapping: RoomMappingConfig = {};

  for (const localRoomId of localRoomIds) {
    // Normalize local room ID for matching (e.g., "junior-suite" -> "junior suite")
    const normalizedLocal = localRoomId
      .toLowerCase()
      .replace(/-/g, ' ')
      .trim();

    // Find best matching Mews room category
    let bestMatch: { id: string; score: number } | null = null;

    for (const mewsRoom of mewsRoomCategories) {
      const mewsName = mewsRoom.Name[languageCode] || Object.values(mewsRoom.Name)[0] || '';
      const normalizedMews = mewsName.toLowerCase().trim();

      // Simple similarity scoring
      let score = 0;

      // Exact match
      if (normalizedMews === normalizedLocal) {
        score = 100;
      } else if (normalizedMews.includes(normalizedLocal) || normalizedLocal.includes(normalizedMews)) {
        // Partial match
        score = 50;
      } else {
        // Word-based matching
        const localWords = normalizedLocal.split(/\s+/);
        const mewsWords = normalizedMews.split(/\s+/);
        const matchingWords = localWords.filter(word => 
          mewsWords.some(mewsWord => mewsWord.includes(word) || word.includes(mewsWord))
        );
        score = (matchingWords.length / Math.max(localWords.length, mewsWords.length)) * 30;
      }

      if (score > 0 && (!bestMatch || score > bestMatch.score)) {
        bestMatch = { id: mewsRoom.Id, score };
      }
    }

    // Only use match if score is reasonable (>= 30)
    if (bestMatch && bestMatch.score >= 30) {
      mapping[localRoomId] = bestMatch.id;
      
      if (import.meta.env.DEV) {
        console.log(
          `[Room Mapping] Auto-matched "${localRoomId}" to Mews room "${mewsRoomCategories.find(r => r.Id === bestMatch!.id)?.Name[languageCode]}" (score: ${bestMatch.score})`
        );
      }
    } else if (import.meta.env.DEV) {
      console.warn(`[Room Mapping] Could not auto-match room: "${localRoomId}"`);
    }
  }

  return mapping;
}
